import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  listDeploymentTemplates,
  createDeploymentTemplate,
  updateDeploymentTemplate,
  deleteDeploymentTemplate,
  getBranding,
  putBranding,
  deleteBranding,
  DeploymentTemplate,
} from '../services/api'
import { errorText } from '../utils/errorText'

// Default config for a new template (mirrors the backend R4 schema).
const emptyConfig = () => ({
  schema_version: 1,
  interactive: true,
  welcome: {
    allowDefer: true,
    deferTimes: 3,
    deferDeadline: null as string | null,
    persistPrompt: true,
    blockExecution: false,
    closeProcessesCountdown: 300,
    customText: '',
    title: '',
    playSound: false,
    promptCondition: 'always' as 'always' | 'ifRunning',
  },
  closeProcesses: [] as string[],
  progress: { enabled: true, statusMessage: 'Installing…' },
  dialogStyle: '' as '' | 'Fluent' | 'Classic',
  restart: { prompt: false, countdownSeconds: 600, customText: '' },
  schedule: { userSelectable: false, maxDelayHours: 72 },
})

interface EditorState {
  id?: string
  name: string
  description: string
  config: ReturnType<typeof emptyConfig>
}

const DeploymentExperience: React.FC = () => {
  const qc = useQueryClient()
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { data: tplData, isLoading } = useQuery({
    queryKey: ['deployment-templates'],
    queryFn: listDeploymentTemplates,
  })
  const { data: brandingData } = useQuery({ queryKey: ['branding'], queryFn: getBranding })

  const saveMut = useMutation({
    mutationFn: async (s: EditorState) => {
      const body = { name: s.name, description: s.description, config_json: s.config }
      return s.id ? updateDeploymentTemplate(s.id, body) : createDeploymentTemplate(body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deployment-templates'] })
      setEditor(null)
      setError(null)
    },
    onError: (e: any) => setError(errorText(e, 'Save failed')),
  })

  const delMut = useMutation({
    mutationFn: deleteDeploymentTemplate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deployment-templates'] }),
  })

  const templates = tplData?.templates ?? []

  const openNew = () => setEditor({ name: '', description: '', config: emptyConfig() })
  const openEdit = (t: DeploymentTemplate) =>
    setEditor({ id: t.id, name: t.name, description: t.description ?? '', config: { ...emptyConfig(), ...t.config_json } })

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-theme-text-primary mb-2">Deployment Experience</h2>
        <p className="text-theme-text-secondary">
          Author how end users see your deployments — defer options, deadlines, close-processes, and branding.
          A template is baked into a deployment at publish time; editing it later does not change already-published apps.
        </p>
      </div>

      {/* Branding */}
      <BrandingSection branding={brandingData?.branding ?? null} />

      {/* Templates */}
      <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-theme-text-primary">Templates</h3>
          <button onClick={openNew} className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg">
            New template
          </button>
        </div>

        {isLoading ? (
          <p className="text-theme-text-tertiary py-4">Loading…</p>
        ) : (
          <div className="space-y-2">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between border border-theme-border-primary rounded-lg px-4 py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-theme-text-primary">{t.name}</span>
                    {t.scope === 'global' && (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-600 text-white">Agentic Packr default</span>
                    )}
                    {t.is_default && <span className="text-xs px-2 py-0.5 rounded bg-blue-600 text-white">default</span>}
                  </div>
                  <p className="text-sm text-theme-text-tertiary">{t.description}</p>
                </div>
                <div className="flex items-center gap-2">
                  {t.scope === 'tenant' ? (
                    <>
                      <button onClick={() => openEdit(t)} className="text-sm text-blue-400 hover:text-blue-300">Edit</button>
                      <button
                        onClick={() => { if (confirm(`Delete template "${t.name}"?`)) delMut.mutate(t.id) }}
                        className="text-sm text-red-400 hover:text-red-300"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <button onClick={() => openEdit(t)} className="text-sm text-theme-text-tertiary hover:text-white">View</button>
                  )}
                </div>
              </div>
            ))}
            {templates.length === 0 && <p className="text-theme-text-tertiary py-4">No templates yet.</p>}
          </div>
        )}
      </div>

      {editor && (
        <TemplateEditor
          state={editor}
          error={error}
          saving={saveMut.isPending}
          readOnly={!!editor.id && templates.find((t) => t.id === editor.id)?.scope === 'global'}
          onChange={setEditor}
          onCancel={() => { setEditor(null); setError(null) }}
          onSave={() => saveMut.mutate(editor)}
        />
      )}
    </div>
  )
}

const BrandingSection: React.FC<{ branding: { product_name: string; logo_url?: string | null } | null }> = ({ branding }) => {
  const qc = useQueryClient()
  const [productName, setProductName] = useState(branding?.product_name ?? '')
  const [logo, setLogo] = useState<File | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  React.useEffect(() => { setProductName(branding?.product_name ?? '') }, [branding])

  const saveMut = useMutation({
    mutationFn: () => putBranding(productName, logo ?? undefined),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['branding'] }); setMsg('Branding saved'); setLogo(null) },
    onError: (e: any) => setMsg(errorText(e, 'Save failed')),
  })
  const clearMut = useMutation({
    mutationFn: deleteBranding,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['branding'] }); setMsg('Branding cleared') },
  })

  return (
    <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6">
      <h3 className="text-lg font-semibold text-theme-text-primary mb-4">Branding</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-theme-text-secondary mb-1">Product / display name</label>
          <input
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            placeholder="e.g. Acme IT"
            className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary"
          />
        </div>
        <div>
          <label className="block text-sm text-theme-text-secondary mb-1">Logo (PNG)</label>
          <input type="file" accept="image/png" onChange={(e) => setLogo(e.target.files?.[0] ?? null)}
                 className="w-full text-sm text-theme-text-secondary" />
          {branding?.logo_url && <p className="text-xs text-theme-text-tertiary mt-1">Current logo set.</p>}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4">
        <button onClick={() => saveMut.mutate()} disabled={!productName || saveMut.isPending}
                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg">
          Save branding
        </button>
        {branding && (
          <button onClick={() => clearMut.mutate()} className="px-3 py-1.5 text-red-400 hover:text-red-300 text-sm">
            Clear (use Agentic Packr default)
          </button>
        )}
        {msg && <span className="text-sm text-theme-text-tertiary">{msg}</span>}
      </div>
    </div>
  )
}

const TemplateEditor: React.FC<{
  state: EditorState
  error: string | null
  saving: boolean
  readOnly: boolean
  onChange: (s: EditorState) => void
  onCancel: () => void
  onSave: () => void
}> = ({ state, error, saving, readOnly, onChange, onCancel, onSave }) => {
  const w = state.config.welcome
  const setW = (patch: Partial<typeof w>) => onChange({ ...state, config: { ...state.config, welcome: { ...w, ...patch } } })

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-theme-secondary border border-theme-border-primary rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-theme-text-primary mb-4">
          {readOnly ? 'View template' : state.id ? 'Edit template' : 'New template'}
        </h3>
        {error && <div className="mb-3 text-sm text-red-400 break-words">{error}</div>}
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-theme-text-secondary mb-1">Name</label>
            <input disabled={readOnly} value={state.name} onChange={(e) => onChange({ ...state, name: e.target.value })}
                   className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
          </div>
          <div>
            <label className="block text-sm text-theme-text-secondary mb-1">Description</label>
            <input disabled={readOnly} value={state.description} onChange={(e) => onChange({ ...state, description: e.target.value })}
                   className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
          </div>
          <label className="flex items-center gap-2 text-sm text-theme-text-primary">
            <input type="checkbox" disabled={readOnly} checked={state.config.interactive}
                   onChange={(e) => onChange({ ...state, config: { ...state.config, interactive: e.target.checked } })} />
            Interactive (show the user a dialog)
          </label>
          <label className="flex items-center gap-2 text-sm text-theme-text-primary">
            <input type="checkbox" disabled={readOnly} checked={w.allowDefer} onChange={(e) => setW({ allowDefer: e.target.checked })} />
            Allow defer
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-theme-text-secondary mb-1">Defer times</label>
              <input type="number" min={0} disabled={readOnly} value={w.deferTimes}
                     onChange={(e) => setW({ deferTimes: parseInt(e.target.value || '0', 10) })}
                     className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
            </div>
            <div>
              <label className="block text-sm text-theme-text-secondary mb-1">Countdown (s)</label>
              <input type="number" min={0} disabled={readOnly} value={w.closeProcessesCountdown}
                     onChange={(e) => setW({ closeProcessesCountdown: parseInt(e.target.value || '0', 10) })}
                     className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-theme-text-secondary mb-1">Deadline (ISO 8601, optional)</label>
            <input disabled={readOnly} value={w.deferDeadline ?? ''} placeholder="2026-07-15T17:00:00Z"
                   onChange={(e) => setW({ deferDeadline: e.target.value || null })}
                   className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
          </div>
          <div>
            <label className="block text-sm text-theme-text-secondary mb-1">Close processes (comma-separated)</label>
            <input disabled={readOnly} value={(state.config.closeProcesses || []).join(',')}
                   onChange={(e) => onChange({ ...state, config: { ...state.config, closeProcesses: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) } })}
                   placeholder="winword,excel"
                   className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
          </div>
          <div>
            <label className="block text-sm text-theme-text-secondary mb-1">Custom text</label>
            <textarea disabled={readOnly} value={w.customText} onChange={(e) => setW({ customText: e.target.value })}
                      className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" rows={2} />
          </div>
          <div>
            <label className="block text-sm text-theme-text-secondary mb-1">Dialog title (optional)</label>
            <input disabled={readOnly} value={(w as any).title ?? ''} onChange={(e) => setW({ title: e.target.value } as any)}
                   placeholder="Defaults to brand + app name"
                   className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm text-theme-text-secondary mb-1">Dialog style</label>
              <select disabled={readOnly} value={(state.config as any).dialogStyle || ''}
                      onChange={(e) => onChange({ ...state, config: { ...state.config, dialogStyle: e.target.value } as any })}
                      className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60">
                <option value="">Package default</option>
                <option value="Fluent">Fluent (modern)</option>
                <option value="Classic">Classic</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-theme-text-secondary mb-1">Show dialog</label>
              <select disabled={readOnly} value={(w as any).promptCondition || 'always'}
                      onChange={(e) => setW({ promptCondition: e.target.value } as any)}
                      className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60">
                <option value="always">Always</option>
                <option value="ifRunning">Only if the app is running</option>
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-theme-text-primary">
            <input type="checkbox" disabled={readOnly} checked={!!(w as any).playSound} onChange={(e) => setW({ playSound: e.target.checked } as any)} />
            Play sound when the dialog appears
          </label>
          <label className="flex items-center gap-2 text-sm text-theme-text-primary">
            <input type="checkbox" disabled={readOnly} checked={!!(state.config as any).schedule?.userSelectable}
                   onChange={(e) => onChange({ ...state, config: { ...state.config, schedule: { ...((state.config as any).schedule || { maxDelayHours: 72 }), userSelectable: e.target.checked } } as any })} />
            Let the user pick an install time (schedule picker)
          </label>
          {(state.config as any).schedule?.userSelectable && (
            <div>
              <label className="block text-sm text-theme-text-secondary mb-1">Latest allowed delay (hours)</label>
              <input type="number" min={1} max={720} disabled={readOnly} value={(state.config as any).schedule?.maxDelayHours ?? 72}
                     onChange={(e) => onChange({ ...state, config: { ...state.config, schedule: { ...((state.config as any).schedule || { userSelectable: true }), maxDelayHours: parseInt(e.target.value || '72', 10) } } as any })}
                     className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
            </div>
          )}
          <label className="flex items-center gap-2 text-sm text-theme-text-primary">
            <input type="checkbox" disabled={readOnly} checked={!!(state.config as any).restart?.prompt}
                   onChange={(e) => onChange({ ...state, config: { ...state.config, restart: { ...((state.config as any).restart || { countdownSeconds: 600 }), prompt: e.target.checked } } as any })} />
            Prompt for restart after install
          </label>
          {(state.config as any).restart?.prompt && (
            <div>
              <label className="block text-sm text-theme-text-secondary mb-1">Restart countdown (s, 0 = no countdown)</label>
              <input type="number" min={0} disabled={readOnly} value={(state.config as any).restart?.countdownSeconds ?? 600}
                     onChange={(e) => onChange({ ...state, config: { ...state.config, restart: { ...((state.config as any).restart || { prompt: true }), countdownSeconds: parseInt(e.target.value || '0', 10) } } as any })}
                     className="w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60" />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onCancel} className="px-3 py-1.5 text-theme-text-secondary hover:text-white text-sm">Cancel</button>
          {!readOnly && (
            <button onClick={onSave} disabled={!state.name || saving}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg">
              {saving ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default DeploymentExperience
